import { useCallback, useMemo } from 'react';
import { cn, Button, Gallery } from '@divlab/divanui';
import { IconChevronRight } from '@divlab/divanui/icons';

import * as ModalWindows from '@Stores/ModalWindows';
import { useDeps } from '@Contexts/DI';
import { useCartShortInfo } from '@Queries/Cart';
import { useCrossSell } from '@Queries/CrossSell';
import useTranslation from '@Queries/useTranslation';
import ProductCard from './elements/ProductCard';
import SkeletWave from '@Skelets/SkeletWave';
import generateProductHash from '@Utils/generateProductHash';
import styles from './CrossSell.module.css';

import type { FC, HTMLAttributes } from 'react';
import type { ProductData } from '@Types/Product';

interface ProductsInInteriorProps extends HTMLAttributes<HTMLDivElement> {
  productId: number;
}

const analyticsTitle = 'Сопутствующие товары карточка товара';

const CrossSell: FC<ProductsInInteriorProps> = (props) => {
  const { className, productId, ...restProps } = props;
  const { t } = useTranslation();
  const { analytics } = useDeps();
  const { data: crossSell = [], isPending, isError } = useCrossSell(productId);
  const { data: shortCard } = useCartShortInfo();

  const newCrossSell: ProductData[] = useMemo(() => {
    const maxQuantityInCategory = 3;

    if (!crossSell?.length) return [];

    // Находим максимальное количество товаров по категориям, ограничиваем maxQuantityInCategory
    const maxCategoryQuantity = crossSell.reduce(
      (max, category) => Math.max(max, category.products.length),
      0,
    );
    const maxQuantity = Math.min(maxQuantityInCategory, maxCategoryQuantity);

    // Формируем список товаров по очереди из каждой категории
    const shortcutProducts: ProductData[] = [];
    for (let i = 0; i < maxQuantity; i += 1) {
      for (let j = 0; j < crossSell.length; j += 1) {
        const product = crossSell[j].products[i];
        if (product) {
          shortcutProducts.push(product);
        }
      }
    }

    // Позиции в корзине, последний добавленный — в начало
    const hashes = shortCard?.positions ? [...shortCard.positions].reverse() : [];
    const cartProductsMap = new Map<string, ProductData>();

    // Собираем продукты из crossSell, которые есть в корзине, по порядку из hashes
    if (hashes.length) {
      for (const hash of hashes) {
        for (const category of crossSell) {
          const product = category.products.find(
            (p) => generateProductHash({ shopProductId: p.id }) === hash,
          );
          if (product) {
            cartProductsMap.set(hash, product);
            break;
          }
        }
      }
    }

    // Получаем cartProducts в порядке из hashes, без пустых значений
    const cartProducts = Array.from(cartProductsMap.values());

    // Добавляем оставшиеся продукты из shortcutProducts, которых нет в cartProducts
    const cartProductIds = new Set(cartProducts.map((p) => p.id));
    const result = [...cartProducts];

    for (const product of shortcutProducts) {
      if (!cartProductIds.has(product.id)) {
        result.push(product);
      }
    }

    return result;
  }, [crossSell, shortCard?.positions]);

  const handleClick = useCallback(() => {
    analytics.dispatchEvent('relatedProduct.openModal');

    ModalWindows.open('CrossSell', {
      productId,
      analyticsTitle,
    });
  }, [analytics, productId]);

  if (isPending) {
    return (
      <div {...restProps} className={cn(styles.skelet, className)}>
        <SkeletWave width='auto' />
      </div>
    );
  }

  if (isError) {
    return null;
  }

  return (
    <div {...restProps} className={cn(styles.wrapper, className)} data-testid='related'>
      <div className={styles.header}>
        <div className={styles.title}>{t('related-title')}</div>
        <Button
          data-testid='show-all'
          size='m'
          className={styles.button}
          view='rounded'
          theme='secondary'
          onClick={handleClick}
        >
          {t('show-more')}
          <IconChevronRight className={styles.iconChevronRight} />
        </Button>
      </div>
      <div className={styles.list}>
        <Gallery
          className={styles.gallery}
          cnSlide={styles.slide}
          cnScrollbar={styles.cnScrollbar}
          spaceBetween={15}
          scrollbar
        >
          {newCrossSell.map((product) => (
            <ProductCard key={product.id} product={product} />
          ))}
        </Gallery>
      </div>
    </div>
  );
};

export default CrossSell;
